package com.essntl.features.category.ui.addeditcategory

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.essntl.uikit.components.button.destructive.EsLargeDestructiveButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.dropdown.EsDropDownMenu
import com.essntl.uikit.components.iconbutton.destructive.EsMediumDestructiveIconButton
import com.essntl.uikit.components.iconbutton.outline.EsMediumOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.image.AsyncUrlIcon
import com.essntl.uikit.components.input.EsInput
import com.essntl.uikit.components.input.label.EsInputLabel
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun AddEditCategoryScreen(
    state: AddEditCategoryState,
    validation: AddEditCategoryValidation,
    onEvent: (AddEditCategoryEvent) -> Unit,
) {

    var isDropDownExpanded by remember {
        mutableStateOf(false)
    }

    val isEdit by remember(state.category.value.id) {
        mutableStateOf(
            state.category.value.id.isNotBlank(),
        )
    }

    var isEditEnabled by remember(state.getCategory.data?.id) { mutableStateOf(false) }

    LaunchedEffect(state.addEditCategory.isSuccess) {
        if (state.addEditCategory.isSuccess) {
            isEditEnabled = false
        }
    }

    LaunchedEffect(state.getCategory.data) {
        validation.addEditCategoryForm.reset()
        validation.addEditCategoryForm.name.value = state.getCategory.data?.name.orEmpty()
        validation.addEditCategoryForm.iconUrl.value = state.getCategory.data?.iconUrl.orEmpty()
    }

    LazyColumn(
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = Modifier
            .fillMaxSize()
            .clip(
                RoundedCornerShape(
                    topStart = 24.dp,
                    topEnd = 24.dp,
                ),
            )
            .padding(
                horizontal = 2.5f.spacing,
            ),
    ) {
        if (
            state.getCategory.isLoading ||
            state.getCategory.isFailure ||
            (isEdit && state.getCategory.data == null)
        )
            item {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier =
                    Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(2.5f.spacing)
                        .clip(RoundedCornerShape(EsConstants.RoundedCornerSize)),
                ) {
                    if (state.getCategory.isLoading) {
                        EsSpinner()
                    } else {
                        Text(
                            text = "Something goes wrong!",
                            color = MaterialTheme.esColorScheme.textSecondary,
                            style = MaterialTheme.esTypography.p1Medium16,
                        )
                    }
                }
            }
        else {
            item {
                Row(
                    horizontalArrangement = Arrangement.spacedBy(1.5.spacing),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Column(
                        modifier = Modifier.padding(bottom = 2.5.spacing),
                    ) {
                        EsInputLabel(label = "Icon")

                        Spacer(modifier = Modifier.height(.5f.spacing))

                        state.category.value.iconUrl?.let {
                            AsyncUrlIcon(
                                url = it,
                                contentDescription = "category url",
                                tint = Color(0xFF8985A2),
                                modifier = Modifier
                                    .size(36.dp)
                                    .clip(RoundedCornerShape(8.dp))
                                    .clickable(
                                        enabled = if (isEdit) isEditEnabled else true,
                                    ) { isDropDownExpanded = true }
                                    .background(
                                        color = Color.Transparent,
                                        shape = RoundedCornerShape(8.dp),
                                    )
                                    .padding(1.spacing),
                            )
                        } ?: if (!validation.addEditCategoryForm.iconUrl.errorMessage.isNullOrEmpty())
                            EsMediumDestructiveIconButton(
                                iconRes = EsIcons.Category,
                                onClick = { isDropDownExpanded = true },
                            )
                        else
                            EsMediumOutlineIconButton(
                                iconRes = EsIcons.Category,
                                enabled = if (isEdit) isEditEnabled else true,
                                loading = state.getCategoryIconsUrl.isLoading,
                                onClick = { isDropDownExpanded = true },
                            )

                        EsDropDownMenu(
                            expanded = isDropDownExpanded,
                            onDismissRequest = {
                                isDropDownExpanded = false
                            },
                            modifier = Modifier.width(200.dp),
                        ) {
                            FlowRow(
                                horizontalArrangement = Arrangement.Start,
                                verticalArrangement = Arrangement.spacedBy(2.5.spacing),
                                modifier = Modifier.fillMaxWidth(),
                            ) {
                                for (categoryUrl in state.getCategoryIconsUrl.data.orEmpty())
                                    AsyncUrlIcon(
                                        url = categoryUrl,
                                        contentDescription = "category url",
                                        tint = Color(0xFF8985A2),
                                        placeholder = { EsSpinner() },
                                        modifier = Modifier
                                            .size(36.dp)
                                            .clip(RoundedCornerShape(8.dp))
                                            .clickable {
                                                validation.addEditCategoryForm.iconUrl.validate(categoryUrl)
                                                onEvent(
                                                    AddEditCategoryEvent.UpdateCategoryFields(
                                                        state.category.value.copy(
                                                            iconUrl = categoryUrl,
                                                        ),
                                                    ),
                                                )
                                                isDropDownExpanded = false
                                            }
                                            .background(
                                                color = Color.Transparent,
                                                shape = RoundedCornerShape(8.dp),
                                            )
                                            .padding(1.spacing),
                                    )
                            }
                        }
                    }

                    EsInput(
                        value = state.category.value.name,
                        state = if (!state.addEditCategory.errorMessage.isNullOrEmpty())
                            EsInputState.Error else
                            validation.addEditCategoryForm.name.state,
                        onValueChange = {
                            validation.addEditCategoryForm.name.validate(it)
                            onEvent(
                                AddEditCategoryEvent.UpdateCategoryFields(
                                    state.category.value.copy(
                                        name = it,
                                    ),
                                ),
                            )
                        },
                        label = "Title",
                        placeholder = "Enter title",
                        message = state.addEditCategory.errorMessage
                            .orEmpty()
                            .ifEmpty { "This field is required" },
                        enabled = if (isEdit) isEditEnabled else true,
                        modifier = Modifier.weight(1f),
                    )
                }
            }

            item {
                Spacer(modifier = Modifier.height(3.spacing))

                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(1.spacing),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    if (isEdit)
                        EsLargeDestructiveButton(
                            onClick = {
                                onEvent(AddEditCategoryEvent.DeleteCategory)
                            },
                            text = "Delete Category",
                            suffixIconRes = EsIcons.Trash,
                            loading = state.countLoading || state.deleteCategory.isLoading,
                            enabled = state.count.toInt() == 0,
                            modifier = Modifier.weight(1f),
                        )

                    EsLargePrimaryButton(
                        onClick = {
                            if (isEdit && !isEditEnabled) {
                                isEditEnabled = true
                            } else if (validation.addEditCategoryForm.validate()) {
                                if (isEdit) {
                                    onEvent(AddEditCategoryEvent.EditCategory)
                                } else {
                                    onEvent(AddEditCategoryEvent.AddCategory)
                                }
                            }
                        },
                        text =
                        if (isEdit) {
                            if (isEditEnabled) {
                                "Update"
                            } else {
                                "Edit"
                            }
                        } else {
                            "Save Category"
                        },
                        loading = state.addEditCategory.isLoading,
                        enabled = if (isEditEnabled) state.isEditEnabled else true,
                        modifier = Modifier.weight(1f),
                    )
                }
            }
        }
    }
}